import EditableAttribute from 'components/shared/EditableAttribute';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import ReviewSpinner from './ReviewSpinner';
import { ReviewType } from '../../../propTypes';
import { apiFetch } from 'utils';
import { duckify, filterDuck } from 'utils/duckify';
import { flashMessages, reviewCriteria as reviewCriteriaDuck, reviews } from 'ducks';

// formats an individual option for a review criteria <select/>
const makeOption = (label, i) => ({
  label: (i + 1).toString() === label ? label : `${i + 1} - ${label}`,
  value: i + 1
});

// return options for a review criteria <select/>
const criteriaOptions = (reviewCriteria, rating) => {
  const criteria = reviewCriteria.find(c => c.id === rating.review_criteria_id);
  return [
    { label: 'None', value: null },
    ...(criteria ? criteria.labels.map(makeOption) : [])
  ];
};

// returns display string for a rating
const formatRating = (reviewCriteria, rating) => {
  if (!rating.rating) {
    return 'Not yet rated';
  }
  const option = criteriaOptions(reviewCriteria, rating)[rating.rating];
  return option ? option.label : 'N/A';
};

// saves rating to a review and resolves to updated rating object
const saveRating = (review, reviewCriteriaId, value) => apiFetch.put(
  {},
  `reviews/${review.id}/rating/${reviewCriteriaId}`,
  { review_rating: { rating: value } }
).then(({ review_rating:reviewRating }) => {
  // splice updated rating into the review
  const ratings = review.review_ratings;
  const i = ratings.findIndex(r => r.review_criteria_id === reviewCriteriaId);
  return {
    ...review,
    review_ratings: [
      ...ratings.slice(0, i),
      reviewRating,
      ...ratings.slice(i + 1)
    ]
  }
});

/**
 * Displays ratings for a review and allows them to be edited.
 *
 * @param review the review object
 * @param reviewCriteria the rating criteria configured for the review year
 * @param cacheReview function to cache a review in redux state
 * @param showFlash function to display a flash message
 * @return {JSX.Element}
 */
const ReviewRatings = ({ review, reviewCriteria, cacheReview, showFlash }) => {

  const [ratings, setRatings] = useState(review.review_ratings);

  useEffect(() => {
    setRatings(review.review_ratings)
  }, [review.id]);

  const setRating = (rating, value) => {
    // splice updated rating into state
    const i = ratings.findIndex(r => r.review_criteria_id === rating.review_criteria_id);
    setRatings([
      ...ratings.slice(0, i),
      { ...rating, rating: value },
      ...ratings.slice(i + 1)
    ]);

    saveRating(review, rating.review_criteria_id, value)
      .then(review => cacheReview(review))
      .catch((e) => {
        showFlash({
          type: 'danger',
          message: 'We\'re sorry, an error occurred while saving the rating. Please reload the page and try ' +
            'again. If the problem persists, please contact support.'
        });
      });
  };

  return (
    <React.Fragment>
      {!reviewCriteria.ready && <ReviewSpinner/>}

      {reviewCriteria.ready && <div className="review-cover-sheet-rating-list">
        {ratings.map(rating =>
          <div className="review-cover-sheet-rating" key={rating.review_criteria_id}>
            <div className="row">
              <div className="col-5">
                <div className="review-cover-sheet-rating-name">{rating.review_criteria_name}</div>
              </div>
              <div className="col-19">
                <div className="review-cover-sheet-rating-value">
                  {review.editable ? <EditableAttribute
                    type="select"
                    entity={rating}
                    attribute="rating"
                    selectOptions={criteriaOptions(reviewCriteria, rating)}
                    emptyText="Not yet rated"
                    required={null}
                    onChange={(_, value) => setRating(rating, value)}
                  /> : formatRating(reviewCriteria, rating)}
                </div>
              </div>
            </div>
          </div>)}
      </div>}
    </React.Fragment>
  );
};

ReviewRatings.propTypes = {
  review: ReviewType.isRequired,
  reviewCriteria: PropTypes.arrayOf(PropTypes.object).isRequired,
  cacheReview: PropTypes.func,
  showFlash: PropTypes.func,
};

const mapDucksToProps = ({ schoolYearId }) => {
  return {
    reviewCriteria: filterDuck(reviewCriteriaDuck, { school_year_id: schoolYearId })
  };
};

const mapDispatchToProps = (dispatch) => ({
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  cacheReview: (review) => dispatch(reviews.actions.cacheSingle(review))
});

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(ReviewRatings);
